import { useEffect, useState } from 'react';
import { isAutomatedBrowser } from '../automatedTraffic';
import { fetchPlanningFeedSummary } from '../services/applicationsApiService';

export const planningSummaryDay = (date = new Date()) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(date);

// One hook in PlanningContext, not one request per badge. Scope changes abort
// old requests; failures and midnight transitions never masquerade as zero.
export function usePlanningFeedSummary(enabled, council, areaId) {
  const shouldFetch = enabled && !isAutomatedBrowser();
  const [day, setDay] = useState(planningSummaryDay);
  const key = JSON.stringify([council, areaId || null, day]);
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!shouldFetch) return;
    const controller = new AbortController();
    let inFlight = false;
    let lastAttempt = 0;
    let refreshAfterMs = 60000;
    const refresh = async () => {
      const currentDay = planningSummaryDay();
      if (currentDay !== day) { setDay(currentDay); return; }
      if (document.hidden || inFlight || Date.now() - lastAttempt < refreshAfterMs) return;
      inFlight = true;
      lastAttempt = Date.now();
      try {
        const data = await fetchPlanningFeedSummary({ council, areaId: areaId || undefined, day,
          signal: controller.signal, retries: 1, timeoutMs: 10000 });
        if (!controller.signal.aborted) setState({ key, data });
        refreshAfterMs = 300000;
      } catch {
        if (!controller.signal.aborted) setState({ key, data: null });
        refreshAfterMs = 60000;
      } finally { inFlight = false; }
    };
    void refresh();
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [shouldFetch, council, areaId, day, key]);
  return shouldFetch && state?.key === key ? state.data : null;
}
